import BigButton from "components/atoms/buttons/BigButton";
import { InputNumber } from "antd";
import { FaCheckCircle } from "react-icons/fa";
import { useFlag } from "@unleash/proxy-client-react";
import * as s from "../UsageSignupStyled";
import {
  includedFeatures,
  TERM_YEAR_OPTIONS,
  UsageSignupFeature,
  UsageSignupFlow,
} from "../useUsageSignupFlow";
import { RiShieldCrossFill, RiPulseLine } from "react-icons/ri";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";
import {
  formatPrice,
  getPrepaidTotal,
  getYearDiscount,
  getYearlyChannelPrice,
  isYearlyBillingCycle,
} from "utils/priceUtils";

interface Props {
  flow: UsageSignupFlow;
}

type MainCycle = BILLING_CYCLE.MONTHLY | BILLING_CYCLE.YEARLY;

type CycleCardMeta = {
  label: string;
  price: string;
  description: string;
  showDiscount?: boolean;
  showChannelInput?: boolean;
  testCycle?: BILLING_CYCLE;
  testLabel?: string;
};

export default function ChannelsStep(props: Props): JSX.Element {
  const { flow } = props;
  const { isUpgrade, paymentData, paymentPrice, termYears } = flow.state;
  const isYearly = isYearlyBillingCycle(paymentData.cycle);
  const isTestYearly = paymentData.cycle === BILLING_CYCLE.TEST_YEARLY;
  const isUnleashEnabled_planCycle3day = useFlag(
    "feature_subscription_planCycle_3day"
  );
  const isUnleashEnabled_planCycle5day = useFlag(
    "feature_subscription_planCycle_5day"
  );

  const yearDiscount = getYearDiscount(paymentPrice.year, termYears);
  const yearlyUnitPrice = getYearlyChannelPrice(
    paymentPrice.channelPrice,
    yearDiscount
  );
  const prepaidTotal = getPrepaidTotal(
    paymentData.channels,
    yearlyUnitPrice,
    termYears
  );
  const formattedChannelPrice = formatPrice(paymentPrice.channelPrice);
  const formattedYearlyChannelPrice = formatPrice(yearlyUnitPrice);

  const cycleCardMeta: Record<MainCycle, CycleCardMeta> = {
    [BILLING_CYCLE.MONTHLY]: {
      label: "Monthly",
      price: `$${formattedChannelPrice}/channel/mo`,
      description: "Usage-based, billed at end of month",
      testCycle: BILLING_CYCLE.TEST_2DAY,
      testLabel: "2 Days",
    },
    [BILLING_CYCLE.YEARLY]: {
      label: "Yearly",
      price: `$${formattedYearlyChannelPrice}/channel/yr`,
      description: "Prepaid upfront, one-time invoice",
      showDiscount: true,
      showChannelInput: true,
      testCycle: BILLING_CYCLE.TEST_YEARLY,
      testLabel: "3 Days",
    },
  };

  const monthlyBullets = [
    "Activate and deactivate End User channels throughout the billing period as needed.",
    "Add/Remove Channels as needed.",
    "At the end of each period, you're billed based on your weighted average usage.",
    "No upfront commitment — scale up or down freely.",
  ];

  const yearlyBullets = [
    "Choose how many channels you need and pay upfront for the full term.",
    "A one-time invoice is generated — no recurring subscription.",
    `Enjoy a ${yearDiscount}% discount compared to monthly billing.`,
  ];

  const isDisabled = (value: BILLING_CYCLE) => {
    if (flow.state.noPermission) {
      return true;
    }
    if (!isUpgrade) return false;

    return !isYearlyBillingCycle(value);
  };

  const isTestCycleEnabled = (cycle: MainCycle) =>
    cycle === BILLING_CYCLE.MONTHLY
      ? isUnleashEnabled_planCycle3day
      : isUnleashEnabled_planCycle5day;

  const isCardActive = (cycle: MainCycle, meta: CycleCardMeta) =>
    paymentData.cycle === cycle ||
    (!!meta.testCycle && paymentData.cycle === meta.testCycle);

  const visibleCycles: MainCycle[] = [
    BILLING_CYCLE.MONTHLY,
    BILLING_CYCLE.YEARLY,
  ];

  const renderTermRow = (
    cycle: MainCycle,
    meta: CycleCardMeta,
    disabled: boolean
  ) => {
    const hasTerm = !!meta.showChannelInput;
    const hasTestCycle = !!meta.testCycle && isTestCycleEnabled(cycle);

    if (hasTerm && !isYearly) return null;
    if (!hasTerm && !hasTestCycle) return null;

    return (
      <div className="term-row" onClick={(e) => e.stopPropagation()}>
        {hasTerm && <label>Prepaid Term</label>}
        <div className="segment-row">
          {hasTerm &&
            TERM_YEAR_OPTIONS.map((year) => (
              <button
                key={year}
                type="button"
                className={[
                  "segment-pill",
                  paymentData.cycle === cycle && termYears === year
                    ? "active"
                    : "",
                ].join(" ")}
                disabled={disabled}
                onClick={() => {
                  if (paymentData.cycle !== cycle) {
                    flow.actions.onChangeBillingCycle(cycle);
                  }
                  flow.actions.onChangeTermYears(year);
                }}
              >
                {year} {year === 1 ? "Year" : "Years"}
              </button>
            ))}
          {!hasTerm && hasTestCycle && (
            <button
              type="button"
              className={[
                "segment-pill",
                paymentData.cycle === cycle ? "active" : "",
              ].join(" ")}
              disabled={disabled}
              onClick={() => flow.actions.onChangeBillingCycle(cycle)}
            >
              Standard
            </button>
          )}
          {hasTestCycle && (
            <button
              type="button"
              className={[
                "segment-pill",
                "segment-pill--qa",
                paymentData.cycle === meta.testCycle ? "active" : "",
              ].join(" ")}
              disabled={disabled}
              onClick={() => flow.actions.onChangeBillingCycle(meta.testCycle!)}
            >
              {meta.testLabel} <span className="qa-tag">QA</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <s.CenterStage>
        <div className="icon channel">
          <RiShieldCrossFill />
        </div>
        <h2>Health Monitoring</h2>
        <p>
          {isYearly
            ? "Prepay for a fixed number of channels for the term at a discounted rate."
            : "Subscribe to usage-based health monitoring based on channels used. Add or remove channels anytime — you only pay for what is used."}
        </p>
      </s.CenterStage>

      <s.ChannelInfoCard>
        <div className="column">
          <h3>What&apos;s included</h3>
          <ul>
            {includedFeatures.map((feature: UsageSignupFeature) => (
              <li key={feature.key}>
                <FaCheckCircle />
                <span>{feature.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="divider" />

        <div className="column">
          <h3>
            <RiPulseLine />
            {isYearly
              ? "How yearly prepaid works"
              : "How usage-based billing works"}
          </h3>
          <ul className="muted">
            {(isYearly ? yearlyBullets : monthlyBullets).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </s.ChannelInfoCard>

      <s.SectionLabel>Billing Cycle</s.SectionLabel>
      <s.CycleGrid>
        {visibleCycles.map((cycle) => {
          const meta = cycleCardMeta[cycle];
          const disabled = isDisabled(cycle);

          return (
            <s.CycleCard
              key={cycle}
              $active={isCardActive(cycle, meta)}
              $disabled={disabled}
              onClick={
                disabled
                  ? undefined
                  : () => flow.actions.onChangeBillingCycle(cycle)
              }
            >
              <div className="header-row">
                <div>
                  {meta.showDiscount ? (
                    <div className="title-row">
                      <h3>{meta.label}</h3>
                      <span className="discount">Save {yearDiscount}%</span>
                    </div>
                  ) : (
                    <h3>{meta.label}</h3>
                  )}
                  <strong>{meta.price}</strong>
                </div>
              </div>
              <p>{meta.description}</p>

              {renderTermRow(cycle, meta, disabled)}

              {meta.showChannelInput && isYearly && (
                <div
                  className="embedded-field-row"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="field-copy">
                    <label>Number of Channels</label>
                  </div>
                  <div className="field-control">
                    <InputNumber
                      min={paymentPrice.registerCamera}
                      value={paymentData.channels}
                      controls
                      onChange={flow.actions.onChangeChannels}
                    />
                    <span>channels</span>
                  </div>
                  {paymentData.channels === 0 && (
                    <p className="field-error">
                      Please enter a number of channels greater than 0.
                    </p>
                  )}
                </div>
              )}
            </s.CycleCard>
          );
        })}
      </s.CycleGrid>

      {isYearly ? (
        <>
          <s.PriceCard>
            <div className="copy">
              <h3>Prepaid total</h3>
              <p>
                {paymentData.channels} channels x ${formattedYearlyChannelPrice}
                /yr
                {isTestYearly
                  ? " x 3 Days"
                  : termYears > 1
                  ? ` x ${termYears} years`
                  : ""}
              </p>
            </div>
            <div className="price">
              <strong>${formatPrice(prepaidTotal)}</strong>
              <span>
                {isTestYearly
                  ? "/3 Days"
                  : termYears > 1
                  ? `/${termYears} years`
                  : "/year"}
              </span>
            </div>
          </s.PriceCard>
        </>
      ) : (
        <s.PriceCard>
          <div className="copy">
            <h3>Your rate</h3>
            <p>Based on weighted average channel count</p>
          </div>
          <div className="price">
            <strong>${formattedChannelPrice}</strong>
            <span>/channel/mo</span>
          </div>
        </s.PriceCard>
      )}

      <s.ButtonRow>
        <BigButton
          label="Continue"
          disabled={flow.state.noPermission || paymentData.channels === 0}
          onClickBtn={() => flow.actions.onChangePage("next")}
        />
      </s.ButtonRow>
    </>
  );
}
